import { DocumentModel } from '../ast/document.types';

export interface HistoryState {
  past: DocumentModel[];
  present: DocumentModel;
  future: DocumentModel[];
  canUndo: boolean;
  canRedo: boolean;
}

const MAX_HISTORY_DEPTH = 40;

export function createInitialHistory(initialDocument: DocumentModel): HistoryState {
  return {
    past: [],
    present: initialDocument,
    future: [],
    canUndo: false,
    canRedo: false,
  };
}

export function pushHistory(
  currentState: HistoryState,
  newDocument: DocumentModel
): HistoryState {
  // If the document hasn't changed meaningfully, return existing
  if (JSON.stringify(currentState.present) === JSON.stringify(newDocument)) {
    return currentState;
  }

  const updatedPast = [...currentState.past, currentState.present];
  if (updatedPast.length > MAX_HISTORY_DEPTH) {
    updatedPast.shift(); // Trim oldest history entry
  }

  return {
    past: updatedPast,
    present: newDocument,
    future: [], // New user edit clears redo future branch
    canUndo: true,
    canRedo: false,
  };
}

export function undoHistory(currentState: HistoryState): HistoryState {
  if (currentState.past.length === 0) return currentState;

  const previous = currentState.past[currentState.past.length - 1];
  const newPast = currentState.past.slice(0, currentState.past.length - 1);

  return {
    past: newPast,
    present: previous,
    future: [currentState.present, ...currentState.future],
    canUndo: newPast.length > 0,
    canRedo: true,
  };
}

export function redoHistory(currentState: HistoryState): HistoryState {
  if (currentState.future.length === 0) return currentState;

  const next = currentState.future[0];
  const newFuture = currentState.future.slice(1);

  return {
    past: [...currentState.past, currentState.present],
    present: next,
    future: newFuture,
    canUndo: true,
    canRedo: newFuture.length > 0,
  };
}
